import {
  Tenancy_Node_Type_Enum_Ps_Enum,
  Tenancy_Rich_Asset_Type_Enum_Ps_Enum,
} from '@pypestream/api-services';

import {
  HANDOFF_TARGET,
  RETURN_TARGET,
  type AgentSpec,
} from '../../server-only/ai/agent-spec';
import { defaultRowData, type TableNode } from '../table-store';

/**
 * Project Bobcat — an agent spec (a template or an AI draft) as table rows,
 * numbered from `numbers` in step order (see `specRowCount`), with every
 * `ref` resolved:
 *
 * - `return` hands the conversation back to the Supervisor: a message that
 *   returns waits for the customer's next question, which the Supervisor
 *   routes (pointing it at the Supervisor's first node instead would re-route
 *   the answer just given), and it ends by offering more help. It waits the
 *   same way without a known Supervisor node (`returnTo` null, a Resource
 *   Library item), so a dropped agent never ends the chat; only a button
 *   that returns needs `returnTo`, and without it the button has no target.
 * - `handoff` points at one shared hand-off step, added at the end only when
 *   something uses it — a Decision node with `xfer_to_agent`.
 * - The engine passes an action exactly the variables in its Variable
 *   column (it does not fill in `{VAR}` in a Parameter Input). So an action's
 *   Variable column lists what it sets and every variable the agent collects
 *   that its Parameter Input names, and the script reads them by those names.
 * - Buttons only route: the engine matches a pressed button, never typed or
 *   spoken words (not even the label), so an answer to keep is asked as a
 *   question instead (see the templates and the draft prompt).
 *
 * The caller allocates the numbers (free ones, next to where the agent sits);
 * the first is the agent's first step.
 */
/** How a finished step invites the next question. */
export const ANYTHING_ELSE = 'Anything else I can help with?';

export const HANDOFF_MESSAGE =
  'Let me connect you with someone on our team. I’ll pass along what you’ve told me, so you won’t have to repeat it.';

const usesHandoff = (spec: AgentSpec): boolean =>
  spec.steps
    .flatMap((step) =>
      step.type === 'A'
        ? step.outcomes.map((o) => o.next)
        : [step.next, ...(step.buttons ?? []).map((b) => b.next)]
    )
    .includes(HANDOFF_TARGET);

const VARIABLE_NAME = /^[A-Z][A-Z0-9_]*$/;

/**
 * An action's Variable column: what it sets, then each variable the agent
 * collects that its Parameter Input names (the engine passes a script only
 * these).
 */
const actionVariables = (
  parameterInput: Record<string, unknown>,
  collected: Set<string>,
  outputs: string[]
): string[] => {
  const reads = Object.values(parameterInput).filter(
    (value): value is string =>
      typeof value === 'string' &&
      VARIABLE_NAME.test(value) &&
      collected.has(value)
  );
  return [...new Set([...outputs, ...reads])];
};

/** How many rows `specToRows` makes: the steps, plus the hand-off if used. */
export const specRowCount = (spec: AgentSpec): number =>
  spec.steps.length + (usesHandoff(spec) ? 1 : 0);

export const specToRows = (
  spec: AgentSpec,
  { numbers, returnTo }: { numbers: number[]; returnTo: number | null }
): TableNode[] => {
  if (numbers.length < specRowCount(spec)) {
    throw new Error('specToRows: not enough node numbers for the spec.');
  }
  const numberOf = new Map<string, number>();
  spec.steps.forEach((step, i) => numberOf.set(step.ref, numbers[i]));
  const handoffNumber = usesHandoff(spec) ? numbers[spec.steps.length] : null;

  /** A target as a node number; null for "nowhere". */
  const resolve = (target: string | undefined): number | null => {
    if (!target) return null;
    if (target === RETURN_TARGET) return returnTo;
    if (target === HANDOFF_TARGET) return handoffNumber;
    return numberOf.get(target) ?? handoffNumber;
  };
  /** Every variable a step of this agent sets. */
  const collected = new Set(spec.steps.flatMap((step) => step.variables ?? []));

  const rows: TableNode[] = spec.steps.map((step, i) => {
    const nodeNumber = numbers[i];
    if (step.type === 'A') {
      const whatsNext = step.outcomes.flatMap((o) => {
        const n = resolve(o.next);
        return n === null ? [] : [`${o.value}~${n}`];
      });
      return {
        ...defaultRowData,
        nodeNumber,
        nodeType: Tenancy_Node_Type_Enum_Ps_Enum.A,
        nodeName: step.name,
        command: step.command,
        parameterInput: JSON.stringify(step.parameterInput ?? {}, null, 2),
        decisionVariable: step.decisionVariable,
        // What the command returns: the compiler requires the decision
        // variable to be one of them.
        output: [step.decisionVariable],
        whatsNext,
        variables: (() => {
          const vars = actionVariables(
            step.parameterInput ?? {},
            collected,
            step.variables ?? []
          );
          return vars.length ? vars : [''];
        })(),
      } as unknown as TableNode;
    }

    const buttons = (step.buttons ?? []).flatMap((b) => {
      const n = resolve(b.next);
      return n === null ? [] : [`${b.label}~${n}`];
    });
    // Back to the Supervisor: wait for the next question rather than jump.
    const returns = step.next === RETURN_TARGET && !buttons.length;
    const next = returns ? null : resolve(step.next);
    return {
      ...defaultRowData,
      nodeNumber,
      nodeType: Tenancy_Node_Type_Enum_Ps_Enum.D,
      nodeName: step.name,
      // Waiting for the next question, it says so — unless it already asks.
      message:
        returns && !/\?\s*$/.test(step.message)
          ? `${step.message.trim()} ${ANYTHING_ELSE}`
          : step.message,
      answerRequired: !!step.answerRequired || buttons.length > 0 || returns,
      nluDisabled: !!step.nluDisabled,
      nextNodes: next === null ? [] : [next],
      variables: step.variables?.length ? step.variables : [''],
      ...(buttons.length
        ? {
            richAssetType: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Button,
            richAssetContent: buttons.join('|'),
            behaviors: ['disable_input'],
          }
        : {}),
    } as TableNode;
  });

  if (handoffNumber !== null) {
    rows.push({
      ...defaultRowData,
      nodeNumber: handoffNumber,
      nodeType: Tenancy_Node_Type_Enum_Ps_Enum.D,
      nodeName: 'Hand off to a person',
      message: HANDOFF_MESSAGE,
      behaviors: ['xfer_to_agent'],
    } as TableNode);
  }

  return rows;
};
